/**
 * Validation Rules for Unusual Configurations
 *
 * Provides visual warnings for unusual or potentially problematic configurations
 * without blocking the user from proceeding.
 */

export interface ValidationRule {
  field: string;
  condition: (value: any, allValues?: any) => boolean;
  severity: 'warning' | 'info';
  message: string;
  recommendation?: string;
}

export const VALIDATIONS: ValidationRule[] = [
  {
    field: 'drivesPerVdev',
    condition: (value: number) => value > 12,
    severity: 'warning',
    message: 'Unusually wide vdev - consider using multiple smaller vdevs',
    recommendation: 'Multiple smaller vdevs provide better IOPS and resilver performance'
  },
  {
    field: 'drivesPerVdev',
    condition: (value: number) => value > 20,
    severity: 'warning',
    message: 'Extremely wide vdev - NOT RECOMMENDED',
    recommendation: 'Strongly recommend using multiple smaller vdevs'
  },
  {
    field: 'poolFillPercent',
    condition: (value: number) => value > 80,
    severity: 'warning',
    message: 'Pools above 80% full have significantly degraded performance',
    recommendation: 'Keep pools below 80% for optimal performance'
  },
  {
    field: 'poolFillPercent',
    condition: (value: number) => value > 90,
    severity: 'warning',
    message: 'Pools above 90% full - CRITICAL performance degradation',
    recommendation: 'URGENT: Add more storage immediately'
  },
  {
    field: 'poolFillPercent',
    condition: (value: number) => value > 95,
    severity: 'warning',
    message: 'Pool fill critically high - may become unstable',
    recommendation: 'DO NOT exceed 95% pool fill'
  },
  {
    field: 'afr',
    condition: (value: number) => value > 3.0,
    severity: 'warning',
    message: 'High failure rate - consider enterprise drives',
    recommendation: 'Consumer drives above 3% AFR have high failure rates'
  },
  {
    field: 'afr',
    condition: (value: number) => value > 5.0,
    severity: 'warning',
    message: 'Very high failure rate - enterprise drives recommended',
    recommendation: 'Consider using enterprise drives for better reliability'
  },
  {
    field: 'ashift',
    condition: (value: number, allValues: any) => {
      const driveSize = allValues?.driveSizeTB || 18; // Default
      return driveSize > 1 && value < 12;
    },
    severity: 'warning',
    message: 'Large drives should use ashift=12 (4KB sectors)',
    recommendation: 'ashift=9 (512B) is only for legacy drives <1TB'
  },
  {
    field: 'compressionRatio',
    condition: (value: number) => value > 2.0,
    severity: 'info',
    message: 'Unrealistic compression ratio - typical is 1.0-1.5 for most data',
    recommendation: 'LZ4 can achieve 2.0+ for highly compressible data'
  },
  {
    field: 'recordsize',
    condition: (value: number, allValues: any) => {
      const workloadType = allValues?.workloadType || 'mixed';
      if (workloadType === 'database' && value > 16) {
        return true;
      }
      if (workloadType === 'media' && value < 128) {
        return true;
      }
      return false;
    },
    severity: 'info',
    message: 'Recordsize mismatched for workload type',
    recommendation: 'Databases: 8-16K, Media: 128K-1M for efficiency'
  }
];

export function validateField(fieldName: string, value: any, allValues?: any): ValidationRule[] {
  return VALIDATIONS.filter(v => v.field === fieldName && v.condition(value, allValues));
}
